import { DOCUMENT } from '@angular/common';
import { Injectable, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { AvisosApiService } from './avisos-api.service';
import { AvisoPortal, ConteoAvisos } from '../models/aviso-portal.model';
import { rutaDestinoSegura } from '../navegacion/ruta-aviso';

/** Cada cuánto se consulta el conteo con la pestaña visible. */
export const CONTEO_CADA_MS = 90_000;
/** Lo mínimo que pasa entre dos consultas del conteo cuando la causa es un cambio de ruta o volver a la pestaña. */
export const CONTEO_MINIMO_MS = 30_000;
/** Tras una acción de la persona (leer, archivar) se confirma el conteo una sola vez, juntando las acciones seguidas. */
const CONTEO_TRAS_ACCION_MS = 1_200;
/** Cuántos avisos muestra el panel de la campana. */
export const LIMITE_PANEL = 20;

export type EstadoPanel = 'cerrado' | 'cargando' | 'ok' | 'error';

/**
 * El estado de las notificaciones nuevas (`/api/avisos`) para la campana de la barra superior. Es solo lo nuevo: los comunicados del módulo antiguo siguen en `NotificacionService`.
 *
 * Cuándo pide cosas al servidor (para no acercarse al límite de 30 por minuto y endpoint; el cliente además se ordena con su propio cupo de 26):
 *  - el CONTEO (liviano): al iniciar, cada 90 s SOLO con la pestaña visible, al volver a una pestaña visible si pasaron más de 30 s, y al cambiar de ruta con un mínimo de 30 s entre
 *    consultas. Una acción de la persona (leer, archivar) lo confirma una vez, 1,2 s después. Nada más lo consulta.
 *  - la LISTA de las últimas 20: solo al abrir el panel de la campana.
 * Marcar como leída y archivar cambian la pantalla de inmediato y, si el servidor las rechaza, la devuelven a como estaba y avisan con el error.
 */
@Injectable({ providedIn: 'root' })
export class AvisosService {
  private api = inject(AvisosApiService);
  private router = inject(Router);
  private doc = inject(DOCUMENT);

  /** Lo que dijo el servidor la última vez, ajustado por lo que la persona hizo desde entonces. */
  readonly conteo = signal<ConteoAvisos>({ noLeidas: 0, hayUrgentes: false });
  /** Se llegó a consultar el conteo al menos una vez. */
  readonly conteoListo = signal(false);
  readonly noLeidas = computed(() => this.conteo().noLeidas);
  readonly hayUrgentes = computed(() => this.conteo().hayUrgentes);

  /** Las últimas 20 notificaciones activas (se piden al abrir el panel). */
  readonly panel = signal<AvisoPortal[]>([]);
  readonly panelEstado = signal<EstadoPanel>('cerrado');
  readonly panelError = signal<string | null>(null);
  /** Lo último que salió mal al marcar o archivar desde el panel (se muestra en el panel). */
  readonly errorAccion = signal<string | null>(null);

  private ultimaConsulta = 0;
  private enVuelo: Promise<void> | null = null;
  private temporizador: ReturnType<typeof setTimeout> | null = null;
  private tras: ReturnType<typeof setTimeout> | null = null;
  private subRuta: Subscription | null = null;
  private alVolver: (() => void) | null = null;
  private activo = false;
  private secuenciaPanel = 0;

  // ── Vigilancia del conteo ───────────────────────────────────────────────────────────────────────

  /** Empieza a vigilar el conteo (la barra superior lo llama al abrirse). Llamarlo otra vez no hace nada. */
  iniciar(): void {
    if (this.activo) return;
    this.activo = true;
    this.subRuta = this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => this.alCambiarDeRuta());
    this.alVolver = () => { if (this.visible() && this.pasaron(CONTEO_MINIMO_MS)) void this.refrescarConteo(); };
    this.doc.addEventListener('visibilitychange', this.alVolver);
    void this.refrescarConteo();
  }

  /** Deja de vigilar y olvida todo (cierre de sesión). */
  detener(): void {
    this.activo = false;
    this.subRuta?.unsubscribe();
    this.subRuta = null;
    if (this.alVolver) this.doc.removeEventListener('visibilitychange', this.alVolver);
    this.alVolver = null;
    if (this.temporizador) clearTimeout(this.temporizador);
    if (this.tras) clearTimeout(this.tras);
    this.temporizador = this.tras = null;
    this.ultimaConsulta = 0;
    this.enVuelo = null;
    this.secuenciaPanel++;
    this.conteo.set({ noLeidas: 0, hayUrgentes: false });
    this.conteoListo.set(false);
    this.panel.set([]);
    this.panelEstado.set('cerrado');
    this.panelError.set(null);
    this.errorAccion.set(null);
  }

  private visible = (): boolean => this.doc.visibilityState !== 'hidden';
  private pasaron = (ms: number): boolean => Date.now() - this.ultimaConsulta >= ms;

  private alCambiarDeRuta(): void {
    if (this.visible() && this.pasaron(CONTEO_MINIMO_MS)) void this.refrescarConteo();
  }

  /** Consulta el conteo ahora (si ya hay una consulta en curso, espera esa). Una falla conserva el último número: la campana no se llena de errores. */
  refrescarConteo(): Promise<void> {
    if (this.enVuelo) return this.enVuelo;
    const p = (async () => {
      try {
        const c = await this.api.conteo();
        if (this.activo) { this.conteo.set(c); this.conteoListo.set(true); }
      } catch { /* se queda el último número; vuelve a intentar en el próximo turno */ } finally {
        this.ultimaConsulta = Date.now();
        this.enVuelo = null;
        if (this.activo) this.programar();
      }
    })();
    this.enVuelo = p;
    return p;
  }

  private programar(): void {
    if (this.temporizador) clearTimeout(this.temporizador);
    this.temporizador = setTimeout(() => {
      this.temporizador = null;
      if (!this.activo) return;
      // Con la pestaña oculta no se consulta: al volver a mostrarse, `visibilitychange` lo hace si pasaron más de 30 s.
      if (this.visible()) void this.refrescarConteo(); else this.programar();
    }, CONTEO_CADA_MS);
  }

  private confirmarConteoPronto(): void {
    if (this.tras || !this.activo) return;
    this.tras = setTimeout(() => { this.tras = null; void this.refrescarConteo(); }, CONTEO_TRAS_ACCION_MS);
  }

  // ── El panel de la campana ──────────────────────────────────────────────────────────────────────

  /** Pide las últimas 20 notificaciones activas (al abrir el panel). Mientras llega, queda a la vista lo que había. */
  async cargarPanel(): Promise<void> {
    const mia = ++this.secuenciaPanel;
    this.panelEstado.set('cargando');
    this.panelError.set(null);
    this.errorAccion.set(null);
    try {
      const pagina = await this.api.listar({ estado: 'activas', limite: LIMITE_PANEL });
      if (mia !== this.secuenciaPanel) return;
      this.panel.set(pagina.items);
      this.panelEstado.set('ok');
    } catch (e) {
      if (mia !== this.secuenciaPanel) return;
      this.panelError.set(mensajeDe(e, 'No se pudieron cargar tus notificaciones. Revisa tu conexión e inténtalo de nuevo.'));
      this.panelEstado.set('error');
    }
  }

  cerrarPanel(): void {
    this.secuenciaPanel++;
    if (this.panelEstado() === 'cargando') this.panelEstado.set(this.panel().length > 0 ? 'ok' : 'cerrado');
    else if (this.panelEstado() !== 'ok') this.panelEstado.set('cerrado');
    this.errorAccion.set(null);
  }

  // ── Acciones ────────────────────────────────────────────────────────────────────────────────────

  /**
   * Marca como leída. La pantalla cambia de inmediato; si el servidor la rechaza vuelve a como estaba y se lanza el error (con su mensaje en español). Si el aviso ya estaba leído no hace nada.
   * Devuelve el aviso como quedó.
   */
  async leer(aviso: AvisoPortal): Promise<AvisoPortal> {
    if (aviso.estado !== 'nueva') return aviso;
    this.errorAccion.set(null);
    const ahora = new Date().toISOString();
    this.cambiarEnPanel(aviso.id, a => ({ ...a, estado: 'leida', leida: true, leidoEn: a.leidoEn ?? ahora }));
    this.sumarNoLeidas(-1);
    try {
      const r = await this.api.leer(aviso.id);
      if (r) this.cambiarEnPanel(aviso.id, () => r);
      this.confirmarConteoPronto();
      return r ?? { ...aviso, estado: 'leida', leida: true, leidoEn: ahora };
    } catch (e) {
      this.cambiarEnPanel(aviso.id, a => ({ ...a, estado: 'nueva', leida: false, leidoEn: aviso.leidoEn }));
      this.sumarNoLeidas(1);
      this.errorAccion.set(mensajeDe(e, 'No se pudo marcar la notificación como leída.'));
      throw e;
    }
  }

  /** Archiva (queda leída y sale de la lista). Si el servidor la rechaza, la notificación vuelve a su lugar. */
  async archivar(aviso: AvisoPortal): Promise<AvisoPortal> {
    this.errorAccion.set(null);
    const eraNueva = aviso.estado === 'nueva';
    const antes = this.panel();
    this.panel.set(antes.filter(a => a.id !== aviso.id));
    if (eraNueva) this.sumarNoLeidas(-1);
    try {
      const r = await this.api.archivar(aviso.id);
      this.confirmarConteoPronto();
      return r ?? { ...aviso, estado: 'archivada', leida: true, archivadoEn: new Date().toISOString() };
    } catch (e) {
      if (antes.some(a => a.id === aviso.id) && !this.panel().some(a => a.id === aviso.id)) {
        this.panel.set([...this.panel(), antes.find(a => a.id === aviso.id)!].sort((a, b) => b.creadoEn.localeCompare(a.creadoEn) || b.id - a.id));
      }
      if (eraNueva) this.sumarNoLeidas(1);
      this.errorAccion.set(mensajeDe(e, 'No se pudo archivar la notificación.'));
      throw e;
    }
  }

  /** Marca como leídas todas las activas (o las de un tipo). Devuelve cuántas cambiaron. Si el servidor la rechaza, todo vuelve a como estaba. */
  async leerTodas(tipo?: string): Promise<number> {
    this.errorAccion.set(null);
    const panelAntes = this.panel();
    const conteoAntes = this.conteo();
    const ahora = new Date().toISOString();
    this.panel.set(panelAntes.map(a => (a.estado === 'nueva' && (!tipo || a.tipo === tipo)) ? { ...a, estado: 'leida', leida: true, leidoEn: ahora } : a));
    if (!tipo) this.conteo.set({ noLeidas: 0, hayUrgentes: false });
    try {
      const n = await this.api.leerTodas(tipo);
      this.confirmarConteoPronto();
      return n;
    } catch (e) {
      this.panel.set(panelAntes);
      this.conteo.set(conteoAntes);
      this.errorAccion.set(mensajeDe(e, 'No se pudieron marcar las notificaciones como leídas.'));
      throw e;
    }
  }

  /**
   * Abre una notificación: la marca como leída (sin esperar) y navega a su pantalla con el router, pero solo si la ruta pasa la validación (ruta del portal que existe en el registro).
   * Devuelve false si no hay a dónde ir (la notificación se muestra, no navega). El cambio de obra, si hace falta, lo resuelve el flujo de siempre (`obraGuard`) con su aviso.
   */
  async abrir(aviso: AvisoPortal): Promise<boolean> {
    const destino = rutaDestinoSegura(aviso.ruta);
    if (aviso.estado === 'nueva') this.leer(aviso).catch(() => { /* el error queda en `errorAccion`; la persona ya está en otra pantalla */ });
    if (!destino) return false;
    return this.router.navigateByUrl(destino);
  }

  // ── Interno ─────────────────────────────────────────────────────────────────────────────────────

  private sumarNoLeidas(delta: number): void {
    this.conteo.update(c => {
      const n = Math.max(0, c.noLeidas + delta);
      return { noLeidas: n, hayUrgentes: n === 0 ? false : c.hayUrgentes };
    });
  }

  private cambiarEnPanel(id: number, f: (a: AvisoPortal) => AvisoPortal): void {
    if (!this.panel().some(a => a.id === id)) return;
    this.panel.update(l => l.map(a => (a.id === id ? f(a) : a)));
  }
}

/** El mensaje en español de un error del servicio (ya viene traducido), o el de respaldo. */
export function mensajeDe(e: unknown, respaldo: string): string {
  const m = (e as { message?: unknown } | null)?.message;
  return typeof m === 'string' && m.trim() ? m : respaldo;
}
